/**
 * 魔力の毎日の配布（2026-10-04）。
 * 魔力は読了では増えない。ログインしていなくても毎日決まった量が届き、ためておける上限で止まる。
 * 上限はユーザーのレベル（初めて読んだ冊数）で育つ。「期限切れで消える」は見せない（上限で止まるだけ）。
 *
 * 数字の正本は Firestore（gachaConfig/magicSupply・管理UIの「ゲームの仕組み › 魔力の配り方」で編集）。
 * 保存が無いとき・壊れているときは DEFAULT_MAGIC_SUPPLY を使う。
 * 管理UI（local-ui）も写しで読むので、import は書かないこと（書くなら相対パスだけ）。
 */

export const MAGIC_SUPPLY_CONFIG_PATH = "gachaConfig/magicSupply";

/** 初めて読んだ冊数が reads 以上になったら、ためておける上限が cap になる */
export interface MagicSupplyLevel {
  reads: number;
  cap: number;
}

export interface MagicSupplyConfig {
  /** 1日に届く魔力 */
  dailyAmount: number;
  /** 日付が変わる時刻（日本時間の時。4 なら朝4時に次の日の分が届く） */
  dayStartHour: number;
  /** レベルの表。1行目がレベル1（reads は 0） */
  levels: MagicSupplyLevel[];
  /**
   * 本のガチャの値段。その日（dayStartHour で切り替わる）の何冊目か順（1行目が1冊目）。
   * 2026-10-25: 引くほど高くなる。精霊の旅と手放すで稼いだ魔力が、読める冊数のインフレにならないように
   */
  drawPrices: number[];
  /** 表より後の冊は、1つ前の値段をこの倍率で上げる */
  drawPriceGrowth: number;
}

/** 仮置き: 1日60（毎日の分だけで本4冊）・7日分から14日分まで育つ。本は1冊ごとに約25〜30%ずつ上がり、10冊目で100 */
export const DEFAULT_MAGIC_SUPPLY: MagicSupplyConfig = {
  dailyAmount: 60,
  dayStartHour: 4,
  levels: [
    { reads: 0, cap: 420 },
    { reads: 50, cap: 480 },
    { reads: 150, cap: 600 },
    { reads: 300, cap: 720 },
    { reads: 500, cap: 840 },
  ],
  drawPrices: [10, 12, 15, 19, 25, 33, 44, 58, 77, 100],
  drawPriceGrowth: 1.3,
};

const toInt = (v: unknown, fallback: number, min: number, max: number) => {
  const n = Math.floor(Number(v));
  return Number.isFinite(n) ? Math.min(max, Math.max(min, n)) : fallback;
};

/** 保存された値を読める形にそろえる（並べ替え・重複の除去・レベル1の行を必ず置く） */
export function parseMagicSupplyConfig(raw: unknown): MagicSupplyConfig {
  const r = (raw && typeof raw === "object" ? raw : {}) as Record<string, unknown>;
  const dailyAmount = toInt(r.dailyAmount, DEFAULT_MAGIC_SUPPLY.dailyAmount, 0, 100000);
  const dayStartHour = toInt(r.dayStartHour, DEFAULT_MAGIC_SUPPLY.dayStartHour, 0, 23);

  const rows = Array.isArray(r.levels) ? r.levels : DEFAULT_MAGIC_SUPPLY.levels;
  const byReads = new Map<number, number>();
  for (const row of rows) {
    if (!row || typeof row !== "object") continue;
    const reads = toInt((row as MagicSupplyLevel).reads, -1, -1, 1000000);
    const cap = toInt((row as MagicSupplyLevel).cap, -1, -1, 10000000);
    if (reads < 0 || cap < 0) continue;
    byReads.set(reads, cap);
  }
  if (!byReads.has(0)) byReads.set(0, DEFAULT_MAGIC_SUPPLY.levels[0].cap);
  const levels = [...byReads.entries()]
    .sort((a, b) => a[0] - b[0])
    .map(([reads, cap]) => ({ reads, cap }));

  const priceRows = Array.isArray(r.drawPrices) ? r.drawPrices : DEFAULT_MAGIC_SUPPLY.drawPrices;
  const parsedPrices = priceRows.map((v) => toInt(v, -1, -1, 10000000)).filter((v) => v >= 0);
  const drawPrices = parsedPrices.length > 0 ? parsedPrices : DEFAULT_MAGIC_SUPPLY.drawPrices;
  const growth = Number(r.drawPriceGrowth);
  const drawPriceGrowth = Number.isFinite(growth) && growth >= 1 && growth <= 10 ? growth : DEFAULT_MAGIC_SUPPLY.drawPriceGrowth;

  return { dailyAmount, dayStartHour, levels, drawPrices, drawPriceGrowth };
}

/** その日にすでに drawsToday 冊引いた人の、次の1冊の値段 */
/** 値段の表（設定そのものでも、画面に届いた配布の様子でもよい） */
export type DrawPriceTable = Pick<MagicSupplyConfig, "drawPrices" | "drawPriceGrowth">;

export function drawPrice(config: DrawPriceTable, drawsToday: number): number {
  const prices = config.drawPrices;
  if (drawsToday < prices.length) return prices[Math.max(0, drawsToday)];
  let price = prices[prices.length - 1];
  for (let i = prices.length; i <= drawsToday; i++) price = Math.ceil(price * config.drawPriceGrowth);
  return price;
}

/** drawsToday 冊引いた後に、points の魔力で今日あと何冊引けるか */
export function affordableDraws(config: DrawPriceTable, drawsToday: number, points: number): number {
  let count = 0;
  let left = points;
  // 値段は必ず上がるので、魔力が尽きるまで数えても回数は小さい（念のため1000冊で止める）
  while (count < 1000) {
    const price = drawPrice(config, drawsToday + count);
    if (price > left || price <= 0) break;
    left -= price;
    count++;
  }
  return count;
}

/** 記録された「何日目に何冊引いたか」から、今日すでに引いた冊数（日が変わっていれば0） */
export function drawsOnDay(d: { novelDrawDay?: unknown; novelDrawCount?: unknown }, today: number): number {
  return d.novelDrawDay === today && typeof d.novelDrawCount === "number" ? d.novelDrawCount : 0;
}

const DAY_MS = 24 * 60 * 60 * 1000;
const JST_OFFSET_MS = 9 * 60 * 60 * 1000;

/** 配布の日の番号（日本時間の dayStartHour 時で切り替わる） */
export function supplyDayIndex(ms: number, dayStartHour: number): number {
  return Math.floor((ms + JST_OFFSET_MS - dayStartHour * 60 * 60 * 1000) / DAY_MS);
}

/** 次の分が届く時刻 */
export function nextSupplyAt(ms: number, dayStartHour: number): number {
  return (supplyDayIndex(ms, dayStartHour) + 1) * DAY_MS - JST_OFFSET_MS + dayStartHour * 60 * 60 * 1000;
}

export interface MagicSupplyLevelInfo {
  level: number;
  cap: number;
  /** いまのレベルになった初読の冊数（ホームのゲージは levelReads から nextReads までの進み） */
  levelReads: number;
  /** 次のレベルに要る初読の冊数（最後のレベルなら null） */
  nextReads: number | null;
  nextCap: number | null;
}

export function supplyLevel(config: MagicSupplyConfig, uniqueReads: number): MagicSupplyLevelInfo {
  let index = 0;
  config.levels.forEach((row, i) => {
    if (uniqueReads >= row.reads) index = i;
  });
  const next = config.levels[index + 1];
  return {
    level: index + 1,
    cap: config.levels[index].cap,
    levelReads: config.levels[index].reads,
    nextReads: next ? next.reads : null,
    nextCap: next ? next.cap : null,
  };
}

/**
 * 届いていない日の分を足す。上限で止めるが、上限より多く持っている分（はじめの魔力・手放したときの報酬など）は減らさない
 */
export function settleFreeMagic(free: number, days: number, dailyAmount: number, cap: number): number {
  if (days <= 0) return free;
  return Math.max(free, Math.min(cap, free + days * dailyAmount));
}

/** 画面に出す配布の様子 */
export interface MagicSupplyStatus extends MagicSupplyLevelInfo {
  dailyAmount: number;
  /** 日付が変わる時刻（日本時間の時） */
  dayStartHour: number;
  /** 初めて読んだ冊数（レベルの元） */
  uniqueReads: number;
  /** 次の分が届く時刻（ミリ秒） */
  nextSupplyAt: number;
  /** 今日すでに引いた本の冊数 */
  drawsToday: number;
  /** 次の1冊の値段 */
  nextDrawPrice: number;
  /** 本の値段の表（今日の残りで何冊引けるかを画面で数えるため） */
  drawPrices: number[];
  drawPriceGrowth: number;
}

/** 画面に出す配布の様子を組み立てる */
export function buildSupplyStatus(
  config: MagicSupplyConfig,
  uniqueReads: number,
  drawsToday: number,
  now: number,
): MagicSupplyStatus {
  return {
    ...supplyLevel(config, uniqueReads),
    dailyAmount: config.dailyAmount,
    dayStartHour: config.dayStartHour,
    uniqueReads,
    nextSupplyAt: nextSupplyAt(now, config.dayStartHour),
    drawsToday,
    nextDrawPrice: drawPrice(config, drawsToday),
    drawPrices: config.drawPrices,
    drawPriceGrowth: config.drawPriceGrowth,
  };
}
